import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageEditMode } from '@/context/PageEditModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline-editable field for DB-backed instrument-lesson columns
 * (name, price, description, details). Renders as plain text when
 * not in edit mode. When admin toggles edit mode:
 *   - Click any wrapped span → turns into an <input> / <textarea>
 *   - Blur or press Enter → PUTs /api/instrument-lessons/{id}
 *   - Escape → cancels
 *
 * Props:
 *   lesson           the lesson object (needs .id)
 *   field            key on the lesson to update
 *   type             'text' | 'number' | 'textarea' (default text)
 *   className        passthrough
 *   prefix / suffix  static decorations (e.g., '$')
 *   onSaved          callback with the new full lesson so parent can update local state
 */
export function LessonInlineField({
  lesson, field, type = 'text', className = '', prefix = '', suffix = '', onSaved, placeholder,
}) {
  const { isAdminUser, enabled } = usePageEditMode();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(lesson[field] ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(lesson[field] ?? ''); }, [lesson, field]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const admin = isAdminUser && enabled;
  const displayVal = lesson[field] ?? '';

  const commit = async () => {
    if (String(value) === String(lesson[field] ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const parsed = type === 'number' ? parseFloat(value) : value;
      await axios.put(`${API}/instrument-lessons/${lesson.id}`,
        { [field]: parsed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Saved');
      onSaved?.({ ...lesson, [field]: parsed });
      // Broadcast so other components using this data (e.g., Step 2 grid) can refresh
      window.dispatchEvent(new CustomEvent('instrument-lessons-updated'));
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!admin) {
    return <span className={className}>{prefix}{displayVal}{suffix}</span>;
  }

  if (editing) {
    const InputEl = type === 'textarea' ? 'textarea' : 'input';
    return (
      <span className="inline-block w-full">
        <InputEl
          ref={inputRef}
          type={type === 'textarea' ? undefined : type}
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && type !== 'textarea') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { setValue(lesson[field] ?? ''); setEditing(false); }
          }}
          placeholder={placeholder}
          className={`${className} bg-stone-800 border border-yellow-400/70 rounded px-1 outline-none focus:border-yellow-400 disabled:opacity-50 w-full`}
          rows={type === 'textarea' ? 3 : undefined}
          data-edit-safe
        />
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`${className} cursor-text outline-dashed outline-2 outline-yellow-400/60 outline-offset-2 rounded-sm hover:bg-yellow-400/10`}
      data-edit-safe
      title="Click to edit"
    >
      {prefix}{displayVal || (placeholder ? <em className="text-yellow-400/60">{placeholder}</em> : ' ')}{suffix}
    </span>
  );
}

export default LessonInlineField;
